import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import type { CustomTableHeaderProps } from '@interxfi/design-system/components/table';
import { PageTabRail } from '../../../../../../../../../../components/page-tab-rail/page-tab-rail';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../../../../../../components/router/router-tabbed-navigation';
import { FundingTransfersTable } from '../../../../../../../../../../features/funding/transfer/funding-transfers-table';
import { getPortfolioCashBalanceDetailPath } from '../../../../../../../../../../features/portfolio/cash-balance/cash-balance-routing';
import { AccountPortfolioCashEquivalentsLayout } from '../../../../../../../../../../features/registration/account/portfolio-value/cash-equivalents/account-portfolio-cash-equivalents-layout';
import type { ViewContext } from '../../../../../../../../../../util/view-context';

export const Route = createFileRoute('/_app/_clearing-core/registration/account/$accountId/_account/_detail/cash/_cash/')({
  staticData: {
    tab: {
      group: TabbedNavigationGroup.RegistrationAccountCashEquivalents,
      label: 'Funding',
      order: 0,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.RegistrationAccountCashEquivalents>,
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `account-cash-${params.accountId}`,
        label: 'Cash & Equivalents',
        to: getPortfolioCashBalanceDetailPath(params.accountId),
      }),
    }) satisfies AggregateBreadcrumb,
  component: AccountCashFundingList,
});

function TableHeader({ filter }: CustomTableHeaderProps) {
  return (
    <PageTabRail
      actions={
        <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
          {filter}
        </HorizontalStack>
      }
      group={TabbedNavigationGroup.RegistrationAccountCashEquivalents}
    />
  );
}

function AccountCashFundingList() {
  const { accountId } = Route.useParams();
  const viewContext = useMemo(
    () =>
      ({
        kind: 'account',
        accountId,
      }) as const satisfies ViewContext,
    [accountId],
  );

  return (
    <AccountPortfolioCashEquivalentsLayout accountId={accountId}>
      <FundingTransfersTable HeaderActions={TableHeader} view={viewContext} />
    </AccountPortfolioCashEquivalentsLayout>
  );
}
